import type {Editor} from 'grapesjs';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerMediaImageModal} from '@EmailMakerReact/email-maker-react.types';

interface SectionPresetConfig {
  id: string;
  columnsPreset: number;
  sortPriority: number;
}

const SECTION_PRESETS: SectionPresetConfig[] = [
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_1, columnsPreset: 1, sortPriority: 0},
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_2, columnsPreset: 2, sortPriority: 1},
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_3, columnsPreset: 4, sortPriority: 2},
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_4, columnsPreset: 8, sortPriority: 3},
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_5, columnsPreset: 9, sortPriority: 4},
  {id: EMAIL_MAKER_BLOCKS.ID_SECTION_PRESET_6, columnsPreset: 11, sortPriority: 5},
];

const TEXT_PRESET_DEFAULT_COLOR = '#242424' as const;
const TEXT_PRESET_BORDER_COLOR = '#FFFFFF' as const;

const BASE_TEXT_PRESET_STYLE = {
  color: TEXT_PRESET_DEFAULT_COLOR,
  display: 'block',
  padding: '10px',
  margin: '0',
  width: 'unset',
  'box-sizing': 'border-box',
  'word-break': 'break-word',
  'overflow-wrap': 'break-word',
  'letter-spacing': 'normal',
  'line-height': '20px',
  'font-family': 'Helvetica, sans-serif',
  opacity: '100%',
  'border-color': TEXT_PRESET_BORDER_COLOR,
  'text-align': 'center',
} as const;

export function importBlocks(editor: Editor): void {
  const bm = editor.BlockManager;

  SECTION_PRESETS.forEach(({id, columnsPreset, sortPriority}) => {
    bm.add(id, {
      activate: true,
      category: EMAIL_MAKER_BLOCKS.CATEGORY_SECTIONS,
      label: window.i18next.t('pmwjs_section'),
      select: true,
      content: {
        type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION,
        [EMAIL_MAKER_PROPS.COLUMNS_PRESET]: columnsPreset,
        [EMAIL_MAKER_PROPS.APPLY_INITIAL_PRESET]: true,
      },
      attributes: {
        trackingEvent: 'EmailMaker_AddSection',
        sortPriority,
      },
    });
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_TABLE, {
    activate: true,
    label: window.i18next.t('pmwjs_table'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE,
    },
    attributes: {
      trackingEvent: 'EmailMaker_AddTable',
      sortPriority: 8,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_DESIGN, {
    activate: true,
    label: window.i18next.t('pmwjs_my_designs'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_MY_DESIGNS,
    attributes: {
      trackingEvent: 'EmailMaker_AddDesign',
      sortPriority: 3,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_UPLOAD_CONTENT, {
    activate: true,
    label: window.i18next.t('pmwjs_myuploads'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_IMAGE,
    attributes: {
      trackingEvent: 'EmailMaker_AddImage',
      sortPriority: 13,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
      [EMAIL_MAKER_PROPS.MEDIA_MODAL]: EmailMakerMediaImageModal.MY_UPLOADS,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_EXPLORE_MEDIA, {
    activate: true,
    label: window.i18next.t('pmwjs_explore_media'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_IMAGE,
    attributes: {
      trackingEvent: 'EmailMaker_AddImage',
      sortPriority: 14,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
      [EMAIL_MAKER_PROPS.MEDIA_MODAL]: EmailMakerMediaImageModal.EXPLORE_MEDIA,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_AI_IMAGES, {
    activate: true,
    label: window.i18next.t('pmwjs_ai_images_small_cap'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_IMAGE,
    attributes: {
      trackingEvent: 'EmailMaker_AddImage',
      sortPriority: 15,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
      [EMAIL_MAKER_PROPS.MEDIA_MODAL]: EmailMakerMediaImageModal.AI_IMAGES,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_STICKER, {
    activate: true,
    label: window.i18next.t('pmwjs_sticker'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    attributes: {
      trackingEvent: 'EmailMaker_AddSticker',
      sortPriority: 11,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_COUNTDOWN, {
    activate: true,
    label: window.i18next.t('pmwjs_countdown'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    attributes: {
      trackingEvent: 'EmailMaker_AddCountdown',
      sortPriority: 12,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_ICONS, {
    activate: true,
    label: window.i18next.t('pmwjs_icon'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    attributes: {
      trackingEvent: 'EmailMaker_AddIcon',
      sortPriority: 10,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_VIDEO, {
    activate: true,
    label: window.i18next.t('pmwjs_video'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    attributes: {
      trackingEvent: 'EmailMaker_AddVideo',
      sortPriority: 8,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_TEXT_PRESET_1, {
    label: window.i18next.t('pmwjs_normal_text'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_TEXT,
    attributes: {
      trackingEvent: 'EmailMaker_AddText',
      sortPriority: 11,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
      style: {
        ...BASE_TEXT_PRESET_STYLE,
        'font-size': '14px',
        'font-weight': 'normal',
      },
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_TEXT_PRESET_2, {
    label: window.i18next.t('pmwjs_title'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_TEXT,
    attributes: {
      trackingEvent: 'EmailMaker_AddText',
      sortPriority: 12,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
      style: {
        ...BASE_TEXT_PRESET_STYLE,
        'font-size': '20px',
        'font-weight': 'bold',
      },
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_BUTTON, {
    label: window.i18next.t('pmwjs_button'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    select: true,
    attributes: {
      trackingEvent: 'EmailMaker_AddButton',
      sortPriority: 6,
    },
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_SOCIAL, {
    label: window.i18next.t('pmwjs_social'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    select: true,
    attributes: {
      trackingEvent: 'EmailMaker_AddSocial',
      sortPriority: 9,
    },
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS,
    },
  });

  bm.add(EMAIL_MAKER_BLOCKS.ID_DIVIDER, {
    label: window.i18next.t('pmwjs_divider'),
    category: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS,
    attributes: {
      trackingEvent: 'EmailMaker_AddDivider',
      sortPriority: 7,
    },
    select: true,
    content: {
      type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER,
    },
  });
}
